/**
 * What this account includes, and whether the two modules see each other's records.
 *
 * **The modules themselves are read-only.** A module is a purchase — some clients buy letting,
 * some buy development, some buy both — so it is ours to set and not something an owner grants
 * themselves from a settings screen. What this offers is a plain answer to "what am I paying for"
 * and somewhere to go to change it. ADR-0080.
 *
 * **Sharing is theirs to set**, and only an owner or an administrator sees the control at all: it
 * is the one setting on this page that changes what every other screen *shows*. Off by default,
 * because an organisation running both businesses is usually running them separately. ADR-0082.
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { MODULE_INFO, type ModuleCode } from "@rentorng/core";
import { api } from "@/lib/api";
import { useApiMutation } from "@/lib/mutations";
import { useModuleGate } from "@/lib/gating";
import { useSession } from "@/lib/session";
import { useModuleLink } from "@/lib/module-rail";
import { Padlock } from "@/components/padlock";
import { Alert, Card, CardTitle, Spinner, Switch } from "@/components/ui";
import { IconCheck, IconLink, IconSplit, IconLock } from "@/components/icons";

interface SharedThing {
  key: string;
  label: string;
  blurb: string;
}

interface ModulesResponse {
  modules: ModuleCode[];
  all: ModuleCode[];
  shareAcrossModules: boolean;
  canShare: boolean;
  shared: SharedThing[];
}

export function ModuleSwitches({ readOnly }: { readOnly?: boolean }) {
  const client = useQueryClient();
  /* Support is a screen both modules share, and this card is drawn on Settings, which both rails
     carry. The link keeps the module it was pressed from. */
  const { modules } = useSession();
  const link = useModuleLink(modules);
  const { data, isLoading } = useQuery({
    queryKey: ["modules"],
    queryFn: () => api.get<ModulesResponse>("/settings/modules"),
  });

  const share = useApiMutation({
    mutationFn: (next: boolean) => api.patch("/settings/modules/sharing", { share: next }),
    invalidates: [["modules"]],
    onSuccess: async () => {
      /* Every list in the product may now be showing a different set of rows, so nothing cached
         survives this. Cheaper than naming the eleven query keys it actually affects and getting
         one of them wrong. */
      await client.invalidateQueries();
    },
  });

  return (
    <>
      <Card>
        <CardTitle hint="What this account is for. Contact us to add or remove a module — it is part of what you pay for.">
          What your account includes
        </CardTitle>

        {isLoading || !data ? (
          <Spinner />
        ) : (
          <div className="space-y-3">
            {data.all.map((code) => (
              <ModuleRow key={code} code={code} included={data.modules.includes(code)} />
            ))}

            <p className="text-xs text-ink-500">
              To add or remove a module,{" "}
              <Link className="font-medium text-brand-700 underline" to={link("/app/support")}>
                contact us
              </Link>
              . No existing records are affected either way.
            </p>
          </div>
        )}
      </Card>

      {/* Drawn only where it can do something. An organisation with one module has nothing to
          share it with, and a control that cannot act is worse than no control. */}
      {data?.canShare ? (
        <Card>
          <CardTitle hint="Whether the two modules work from one set of records or keep separate books.">
            Sharing between modules
          </CardTitle>

          {share.errorMessage ? (
            <div className="mb-4">
              <Alert tone="danger">{share.errorMessage}</Alert>
            </div>
          ) : null}

          <div className="flex flex-wrap items-start gap-3 rounded-[var(--radius-control)] border border-ink-200 p-3">
            <div className="pt-0.5 text-ink-400">
              {data.shareAcrossModules ? <IconLink size={20} /> : <IconSplit size={20} />}
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-medium">
                {data.shareAcrossModules ? "Records are shared" : "Records are kept separate"}
              </p>
              <p className="mt-0.5 text-sm text-ink-500">
                {data.shareAcrossModules
                  ? "Both modules work from one set of clients, payments and accounts."
                  : "Each module has its own clients, payments and accounts. Neither can see the other's, including when searching."}
              </p>
            </div>
            <div className="pt-1">
              <Switch
                checked={data.shareAcrossModules}
                disabled={readOnly || share.isPending}
                label="Share records between modules"
                onChange={(next) => share.mutate(next)}
              />
            </div>
          </div>

          {/* Named explicitly, because "shared" without a list is a setting nobody can predict the
              effect of. Everything not listed belongs to one module and is separate regardless. */}
          <ul className="mt-3 grid gap-2 sm:grid-cols-2">
            {data.shared.map((thing) => (
              <li key={thing.key} className="text-xs text-ink-500">
                <span className="font-medium text-ink-700">{thing.label}</span> — {thing.blurb}
              </li>
            ))}
          </ul>

          <p className="mt-3 text-xs text-ink-500">
            A letting portfolio, its units and tenancies — and a development's properties, plots and
            purchases — always belong to one module and are never shared.
          </p>
        </Card>
      ) : null}
    </>
  );
}

/**
 * One module, and whether this account holds it.
 *
 * **A module it does not hold is drawn with the padlock, not with an empty circle.** It used to be
 * an unticked box and the words "Not included", which is accurate and says nothing: it reads as a
 * setting somebody forgot to switch on. The padlock says the same fact in the vocabulary the rest
 * of the product now uses for everything shut, and pressing it says what would open it — which for
 * a module is a conversation rather than a plan, because `orgs.modules` is the purchase and no
 * rung on the pricing ladder adds it. The server says exactly that in `gate.message`, and it is
 * shown as it arrives.
 *
 * Its own component because the gate is a hook, and a hook cannot be called inside a `.map`.
 */
function ModuleRow({ code, included }: { code: ModuleCode; included: boolean }) {
  const info = MODULE_INFO[code];
  const gate = useModuleGate(code);

  return (
    <div
      className={`flex flex-wrap items-start gap-3 rounded-[var(--radius-control)] border p-3 ${
        included ? "border-brand-200 bg-brand-50/40" : "border-ink-200"
      }`}
    >
      <div className="pt-0.5">
        {included ? (
          <span className="flex size-5 items-center justify-center rounded-full bg-brand-600 text-white">
            <IconCheck size={13} />
          </span>
        ) : (
          <span className="flex size-5 items-center justify-center rounded-full border border-ink-300 text-ink-400">
            <IconLock size={12} />
          </span>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-center gap-2 font-medium">
          {info.label}
          {/* Not included, and pressable — the padlock carries the sentence and the way forward.
              A gate arrives only for a module this organisation does not hold. */}
          {gate ? <Padlock gate={gate} label="Not included" /> : null}
        </p>
        <p className="mt-0.5 text-sm text-ink-500">{info.blurb}</p>
      </div>
    </div>
  );
}
